/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {EventEmitter} from 'events';

/**
 * Class that listens to all events on the Shell.
 */
export default class EventObserver extends EventEmitter {
  /**
   * Initialise EventObservers and related observers.
   */
  constructor() {
    super();
    this.blacklistedTags = ['INPUT', 'TEXTAREA']; // don't emit event when user is typing
    this.whitelistedClasses = ['.spectrum-Shell-feedback-button'];
    this.preventDefaultCombos = [];
  }

  /**
   * Checks if blacklistTags contains the input element's tagname.
   * @param {Object} element The HTML DOM element.
   * @returns {boolean} Returns true if the tag is in blacklistTags.
   */
  isBlacklistedTag = (element) => {
    return this.blacklistedTags.includes(element.tagName);
  }

  /**
   * Checks if the element contains at least one class from a list of classes.
   * @param {Object} element The HTML DOM element.
   * @returns {string} Returns className if the element contains the class.
   */
  containsClass = (classes, element) => {
    return classes.find(className => element.closest(className));
  }

  /**
   * Mouse onClick Handler.
   * @param {Event} e MouseEvent.
   */
  handleClick = (e) => {
    let whitelistClass = this.containsClass(this.whitelistedClasses, e.target);
    let isTrackingRequired = !!whitelistClass || !this.isBlacklistedTag(e.target);

    if (!e || !isTrackingRequired) {
      return;
    }
    const {className, textContent} = e.target;
    this.emit('click', {
      data: Object.assign({className, textContent}, e.data || {}),
      type: e.type
    });
  }

  /**
   * Key press handler.
   * @param {Event} e Key event.
   */
  handleKeyPress = (e) => {
    let element = document.activeElement;
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      this.checkAgainstCombos(e);
    }
    if (this.isBlacklistedTag(element)) {
      return;
    }
    this.emit('keyboard', {
      altKey: e.altKey,
      bubbles: e.bubbles,
      ctrlKey: e.ctrlKey,
      isComposing: e.isComposing,
      key: e.key,
      keyCode: e.keyCode,
      location: e.location,
      metaKey: e.metaKey,
      repeat: e.repeat,
      shiftKey: e.shiftKey,
      type: e.type
    });
  }

  /**
   * Checks pressed key against configured list of key combinations
   * to prevent default on. Combos can be any number of modifier keys and a single
   * keycode.
   * @param {Event} e Key event.
   */
  checkAgainstCombos = (e) => {
    for (let combo of this.preventDefaultCombos) {
      if (
        e.key === combo['key'] &&
        (combo.altKey ? e.altKey : !e.altKey) &&
        (combo.ctrlKey ? e.ctrlKey : !e.ctrlKey) &&
        (combo.metaKey ? e.metaKey : !e.metaKey) &&
        (combo.shiftKey ? e.shiftKey : !e.shiftKey)
      ) {
        e.preventDefault();
        return;
      }
    }
  }

  /**
   * Sets the array of key combination objects.
   * @param {Array} arr An array of combo objects.
   */
  setPreventDefaultCombos = (arr) => {
    this.preventDefaultCombos = arr;
  }

  clearPreventDefaultCombos = () => {
    this.preventDefaultCombos = [];
  }

  /**
   * Removes listeners for events from the window object.
   */
  unwatch() {
    this.clearPreventDefaultCombos();
    window.removeEventListener('keydown', this.handleKeyPress);
    window.removeEventListener('keyup', this.handleKeyPress);
    window.removeEventListener('click', this.handleClick);
  }

  /**
   * Add listeners for events to the window object.
   */
  watch() {
    window.addEventListener('keydown', this.handleKeyPress);
    window.addEventListener('keyup', this.handleKeyPress);
    window.addEventListener('click', this.handleClick);
  }
}
